import React, { useEffect, useState } from 'react';
import { Grid, useMediaQuery } from '@mui/material';
import { PrimaryButton, Text, SizeableRoundedDialog } from 'library';
import theme from 'theme';
import { BusinessJobResponse } from './models';
import useLoading from 'library/hooks/useLoading';
import { client, consoleLogInDev } from 'shared';
import useForm from 'parent-portal/forms/components/FormHook';
import { JobDetailValue, JobQualificationValue, Qualification } from 'models';
import JobDetailInput from 'parent-portal/forms/components/pages/HeadlineDescriptionPage/JobDetailInput';
import FormField from 'parent-portal/forms/components/FormField';
import { JobDetail } from 'models';
import QualificationInput from 'parent-portal/forms/components/pages/BusinessJobRequirementsPage/QualificationInput';
import AdditionalQualifications from 'parent-portal/forms/components/pages/BusinessJobRequirementsPage/AdditionalQualifications';

export default function EditDetailsModal({
    open,
    close,
    businessJobResponse,
    onSaveComplete,
}: {
    open: boolean;
    close: () => void;
    businessJobResponse: BusinessJobResponse;
    onSaveComplete: (title: string, description: string) => void;
}) {
    const small = useMediaQuery(theme.breakpoints.down('sm'));
    const form = useForm<{
        jobDetails: JobDetailValue[];
        jobQualifications: JobQualificationValue[];
    }>({
        jobDetails: businessJobResponse.job_details.details.map((x) => {
            return {
                detail: x.detail,
                option: x.option,
                value: x.value,
            };
        }),
        jobQualifications: businessJobResponse.job_details.qualification_requirements.map((x) => {
            return { qualification: x.qualification, option: x.option, value: x.value };
        }),
    });
    const { loading, setLoading } = useLoading();
    const [title, setTitle] = useState<string>(businessJobResponse.job_details.headline);
    const [description, setDescription] = useState(businessJobResponse.job_details.family_comments);
    const [details, setDetails] = useState<JobDetail[]>([]);
    const [qualifications, setQualifications] = useState<Qualification[]>([]);

    const { jobDetails, jobQualifications } = form.fieldStates;
    const { value, setValue } = jobQualifications;

    const getJobDetails = () => {
        if (details.length) {
            return;
        }

        client('api/job-detail/').then(setDetails).catch(consoleLogInDev);
    };

    const getQualifications = () => {
        if (qualifications.length) {
            return;
        }

        client('api/qualification/').then(setQualifications).catch(consoleLogInDev);
    };

    useEffect(() => {
        getJobDetails();
        getQualifications();
    }, []);

    useEffect(() => {
        setTitle(businessJobResponse.job_details.headline);
        setDescription(businessJobResponse.job_details.family_comments);
    }, [businessJobResponse]);

    function submit() {
        setLoading(true);
        client(`api/ongoing/${businessJobResponse?.job_details?.id}/`, {
            method: 'PATCH',
            body: { qualification_requirements: jobQualifications.value, job_details: jobDetails.value },
        })
            .then(() => onSaveComplete(title, description))
            .finally(() => {
                setLoading(false);
                close();
            });
    }

    return (
        <SizeableRoundedDialog
            open={open}
            onClose={close}
            fullScreen={small}
            closeButton
            maxWidth="md"
            style={{ maxWidth: 500 }}
        >
            <Grid container item direction="column" style={{ padding: 20, gap: 10 }}>
                <Text variant="h1">Update Details</Text>
                {details.map((detail) => (
                    <FormField title={detail.name} key={detail.id}>
                        <JobDetailInput detail={detail} jobDetails={jobDetails} />
                    </FormField>
                ))}
                {qualifications
                    .filter((qual) => !qual.web_view_options.nested)
                    .map((qual) => (
                        <FormField title={qual.web_view_options.header} key={qual.id}>
                            {qual.web_view_options.sub_header && (
                                <Text variant="body1" bold textStyle={{ marginTop: 10 }}>
                                    {qual.web_view_options.sub_header}
                                </Text>
                            )}
                            <QualificationInput qualification={qual} jobQualifications={jobQualifications} />
                        </FormField>
                    ))}
                <AdditionalQualifications
                    qualifications={qualifications.filter((qual) => qual.web_view_options.nested)}
                    jobQualifications={jobQualifications}
                />
                <PrimaryButton
                    loading={loading}
                    disabled={loading}
                    onClick={submit}
                    buttonStyle={{ width: undefined, alignSelf: 'flex-end' }}
                >
                    Save
                </PrimaryButton>
            </Grid>
        </SizeableRoundedDialog>
    );
}
